import {
  ArrowsClockwise,
  ChatCircle,
  Info,
  Lock,
  Queue as QueueIcon,
  UsersThree,
  WarningCircle,
  WifiSlash,
  MusicNotesSimple,
} from '@phosphor-icons/react';
import { Tabs } from 'radix-ui';
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router';
import { Chat } from '../components/room/Chat';
import { NowPlaying, RoomActions } from '../components/room/Controls';
import { PeoplePanel, QueuePanel } from '../components/room/Panels';
import { RoomPlayer } from '../components/room/Player';
import { PlaylistPanel } from '../components/room/PlaylistPanel';
import { RoomDialogs } from '../components/room/RoomDialogs';
import { RoomInfo } from '../components/room/RoomInfo';
import { Stage } from '../components/room/Stage';
import { useSession } from '../lib/queries';
import { RoomConnection } from '../room/connection';
import { RoomConnectionContext } from '../room/context';
import { useRoomStore } from '../room/store';
import { Button, cx, EmptyState, LinkButton, Skeleton } from '../ui/kit';

function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(query);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(query).matches,
  );
}

export function RoomPage() {
  const { slug = '' } = useParams();
  const session = useSession().data;
  const identity = session ? (session.user?.id ?? session.guest?.id ?? 'anon') : null;
  const [conn, setConn] = useState<RoomConnection | null>(null);

  useEffect(() => {
    if (!identity) return;
    const c = new RoomConnection(slug);
    setConn(c);
    return () => {
      c.stop();
      setConn(null);
    };
  }, [slug, identity]);

  const status = useRoomStore((s) => s.status);
  const data = useRoomStore((s) => s.data);
  const error = useRoomStore((s) => s.error);
  const notice = useRoomStore((s) => s.notice);

  useEffect(() => {
    document.title = data ? `${data.room.name} · CrowdSet` : 'CrowdSet';
    return () => {
      document.title = 'CrowdSet';
    };
  }, [data?.room.name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!conn) return <RoomSkeleton />;

  let body: ReactNode;
  if (status === 'error' && error)
    body = <RoomGate code={error.code} message={error.message} onRetry={() => conn.retry()} />;
  else if (status === 'ended' && notice)
    body = (
      <RoomEnded
        kind={notice.kind}
        message={notice.message}
        onRejoin={() => window.location.reload()}
      />
    );
  else if (!data || data.room.slug !== slug) body = <RoomSkeleton />;
  else body = <RoomView />;

  return <RoomConnectionContext.Provider value={conn}>{body}</RoomConnectionContext.Provider>;
}

function RoomView() {
  const desktop = useMedia('(min-width: 1024px)');
  return (
    <>
      <ConnectionBanner />
      {desktop ? (
        <div className="grid min-h-[calc(100dvh-56px)] grid-cols-[minmax(0,1fr)_minmax(340px,380px)]">
          <div className="flex min-h-0 flex-col gap-3 p-3">
            <div className="flex min-h-[560px] flex-1 flex-col">
              <Stage
                player={
                  <div className="w-full max-w-[min(820px,max(480px,calc((100dvh-590px)*16/9)))]">
                    <RoomPlayer />
                  </div>
                }
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[1.25rem] border border-white/[0.06] bg-ink-900 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <NowPlaying />
              <RoomActions />
            </div>
          </div>
          <aside className="sticky top-14 flex h-[calc(100dvh-56px)] flex-col border-l border-white/[0.06] bg-ink-900/60">
            <RoomHeader />
            <SideTabs />
          </aside>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="sticky top-14 z-20 border-b border-white/[0.06] bg-ink-950/95 p-2 backdrop-blur">
            <RoomPlayer compact />
          </div>
          <div className="flex flex-col gap-3 px-3 py-3">
            <RoomHeader compact />
            <NowPlaying compact />
            <RoomActions layout="stack" />
          </div>
          <div className="h-[260px]">
            <Stage compact player={null} />
          </div>
          <div className="flex h-[70dvh] flex-col border-t border-white/[0.06]">
            <SideTabs />
          </div>
        </div>
      )}
      <RoomDialogs />
    </>
  );
}

function SideTabs() {
  const queueLen = useRoomStore((s) => s.data?.queue.length ?? 0);
  const people = useRoomStore((s) => s.data?.participants.length ?? 0);
  const [tab, setTab] = useState('chat');
  const trigger =
    'relative flex h-11 flex-1 items-center justify-center gap-1.5 text-xs text-fg-muted outline-none transition-colors hover:text-fg data-[state=active]:text-fg focus-visible:bg-white/[0.04] after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:bg-transparent data-[state=active]:after:bg-accent';
  return (
    <Tabs.Root value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
      <Tabs.List
        aria-label="Room panels"
        className="flex shrink-0 border-b border-white/[0.06] px-1"
      >
        <Tabs.Trigger value="chat" className={trigger}>
          <ChatCircle size={15} aria-hidden /> Chat
        </Tabs.Trigger>
        <Tabs.Trigger value="queue" className={trigger}>
          <QueueIcon size={15} aria-hidden /> Queue
          {queueLen > 0 && <span className="font-mono text-[10px] text-fg-subtle">{queueLen}</span>}
        </Tabs.Trigger>
        <Tabs.Trigger value="people" className={trigger}>
          <UsersThree size={15} aria-hidden /> People{' '}
          <span className="font-mono text-[10px] text-fg-subtle">{people}</span>
        </Tabs.Trigger>
        <Tabs.Trigger value="playlist" className={trigger}>
          <MusicNotesSimple size={15} aria-hidden />{' '}
          <span className="hidden sm:inline">Playlist</span>
          <span className="sm:hidden">Tracks</span>
        </Tabs.Trigger>
        <Tabs.Trigger value="room" className={trigger}>
          <Info size={15} aria-hidden /> Room
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content
        value="chat"
        className="min-h-0 flex-1 outline-none"
        forceMount
        hidden={tab !== 'chat'}
      >
        <Chat />
      </Tabs.Content>
      <Tabs.Content value="queue" className="min-h-0 flex-1 outline-none">
        <QueuePanel />
      </Tabs.Content>
      <Tabs.Content value="people" className="min-h-0 flex-1 outline-none">
        <PeoplePanel />
      </Tabs.Content>
      <Tabs.Content value="playlist" className="min-h-0 flex-1 outline-none">
        <PlaylistPanel />
      </Tabs.Content>
      <Tabs.Content value="room" className="min-h-0 flex-1 outline-none">
        <RoomInfo />
      </Tabs.Content>
    </Tabs.Root>
  );
}

function ConnectionBanner() {
  const status = useRoomStore((s) => s.status);
  const server = useRoomStore((s) => s.serverStatus);
  const degraded = server && (server.realtime !== 'ok' || server.database !== 'ok');
  if (status === 'live' && !degraded) return null;
  const text =
    status === 'reconnecting'
      ? 'Reconnecting… What you see may be out of date until the room state refreshes.'
      : status === 'offline'
        ? 'You are offline. Playback continues locally but is not following the room.'
        : status === 'joining' || status === 'connecting'
          ? 'Rejoining the room…'
          : degraded
            ? (server?.message ??
              'Live updates are degraded. The room will resync when service recovers.')
            : null;
  if (!text) return null;
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-warn/20 bg-warn/[0.08] px-4 py-2 text-sm text-warn"
    >
      {status === 'offline' ? (
        <WifiSlash size={16} aria-hidden />
      ) : (
        <ArrowsClockwise size={16} className="motion-safe:animate-spin" aria-hidden />
      )}
      {text}
    </div>
  );
}

function RoomSkeleton() {
  return (
    <div
      className="grid min-h-[calc(100dvh-56px)] grid-cols-1 gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_380px]"
      aria-busy="true"
      aria-label="Loading room"
    >
      <div className="flex flex-col gap-3">
        <Skeleton className="min-h-[520px] flex-1 rounded-[1.5rem]" />
        <Skeleton className="h-20 rounded-[1.25rem]" />
      </div>
      <Skeleton className="hidden rounded-[1.25rem] lg:block" />
    </div>
  );
}

const GATE: Record<string, { title: string; body: string; icon: 'lock' | 'warn' }> = {
  ROOM_FULL: {
    title: 'This room is full',
    body: 'It has reached its participant limit. Try again in a few minutes or pick another room.',
    icon: 'warn',
  },
  BANNED: {
    title: 'You cannot join this room',
    body: 'A moderator banned you from this room.',
    icon: 'lock',
  },
  FORBIDDEN: {
    title: 'This room is private',
    body: 'You need an invite from the owner to join. Ask them for a link.',
    icon: 'lock',
  },
  UNAUTHENTICATED: {
    title: 'Sign in to enter this room',
    body: 'Private rooms are for signed-in members with an invite.',
    icon: 'lock',
  },
  VERIFIED_ACCOUNT_REQUIRED: {
    title: 'Verify your account',
    body: 'Confirm your email address to join this room.',
    icon: 'lock',
  },
  NOT_FOUND: {
    title: 'Room not found',
    body: 'The link may be wrong, or the room no longer exists.',
    icon: 'warn',
  },
  ROOM_UNAVAILABLE: {
    title: 'This room is unavailable',
    body: 'It was archived or suspended.',
    icon: 'warn',
  },
};

function RoomGate({
  code,
  message,
  onRetry,
}: {
  code: string;
  message: string;
  onRetry: () => void;
}) {
  const loc = useLocation();
  const g = GATE[code] ?? {
    title: 'Could not join the room',
    body: message,
    icon: 'warn' as const,
  };
  return (
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-16">
      <EmptyState
        art={
          g.icon === 'lock' ? (
            <Lock size={28} className="text-accent" aria-hidden />
          ) : (
            <WarningCircle size={28} className="text-warn" aria-hidden />
          )
        }
        title={g.title}
        body={g.body}
        action={
          <div className="flex flex-wrap gap-2">
            {code === 'UNAUTHENTICATED' ? (
              <LinkButton
                variant="primary"
                to={`/signin?returnTo=${encodeURIComponent(loc.pathname)}`}
              >
                Sign in
              </LinkButton>
            ) : (
              code === 'ROOM_FULL' && (
                <Button variant="primary" onClick={onRetry}>
                  Try again
                </Button>
              )
            )}
            <LinkButton to="/">Browse rooms</LinkButton>
          </div>
        }
      />
    </div>
  );
}

const ENDED_TITLE: Record<string, string> = {
  moved_to_other_room: 'You joined another room',
  kicked: 'You were removed from the room',
  banned: 'You were banned from this room',
  signed_out: 'You signed out',
  session_expired: 'Your session expired',
  room_archived: 'This room was archived',
  room_suspended: 'This room was suspended',
  access_revoked: 'Your access was revoked',
};

function RoomEnded({
  kind,
  message,
  onRejoin,
}: {
  kind: string;
  message: string;
  onRejoin: () => void;
}) {
  const canRejoin =
    kind === 'moved_to_other_room' ||
    kind === 'kicked' ||
    kind === 'session_expired' ||
    kind === 'signed_out';
  return (
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-16">
      <EmptyState
        art={
          <WarningCircle
            size={28}
            className={cx(kind === 'banned' ? 'text-danger' : 'text-warn')}
            aria-hidden
          />
        }
        title={ENDED_TITLE[kind] ?? 'You left the room'}
        body={message}
        action={
          <div className="flex gap-2">
            {canRejoin && (
              <Button variant="primary" onClick={onRejoin}>
                {kind === 'moved_to_other_room' ? 'Bring me back here' : 'Rejoin'}
              </Button>
            )}
            <LinkButton to="/">Browse rooms</LinkButton>
          </div>
        }
      />
    </div>
  );
}

/** Which room am I in? Name, visibility and head-count, always visible. */
function RoomHeader({ compact = false }: { compact?: boolean }) {
  const room = useRoomStore((s) => s.data?.room);
  const count = useRoomStore((s) => s.data?.participants.length ?? 0);
  if (!room) return null;
  return (
    <div
      className={
        compact
          ? 'flex items-baseline justify-between gap-3'
          : 'flex items-baseline justify-between gap-3 border-b border-white/[0.06] px-4 pb-2 pt-3'
      }
    >
      <h1 className="min-w-0 truncate text-base font-semibold tracking-tight">{room.name}</h1>
      <p className="shrink-0 text-xs text-fg-subtle">
        {room.visibility === 'private' ? 'Private · ' : ''}
        <span className="font-mono">{count}</span>/{room.capacity} here
      </p>
    </div>
  );
}
